import * as THREE from 'three';
import {touchToMouse} from "../misc";
import {CustomOrbitControls} from "./orbit-controls";
import {Viewer} from '../main';
import {Vector3} from 'three';
import { CameraAlignerAdv, CameraAlignerBasic } from '../image-aligner/image-aligner';
import { eventToPixel } from '../utilities';
import { SceneCoordinate, SphericalPosition } from '../projections';

export class PanoControls {
  public viewer: Viewer;
  public object: any;
  public domElement: any;
  public cameraAlignerAdv: CameraAlignerAdv;
  public cameraAlignerBasic: CameraAlignerBasic;
  private minFOV = 10;
  public maxFOV = 90;
  public orbitDefaultApplied = false;
  private time: any;
  private enabled: any;
  private rotateSpeed: any;
  public direction: Vector3;
  private up: any;
  public orbit: any;
  public flyControls: any;
  private mouse: any;
  private wheelDelta: any;
  private phiDelta: any;
  private thetaDelta: any;
  private assetSelected: any;
  private moveEvent: any;
  private zooming: any;
  private contextMenuClick;
  public alignerScene: THREE.Scene

  constructor(viewer, object, domElement, props) {
    const {contextMenuClick} = props;

    this.viewer = viewer;
    this.object = object;
    this.domElement = (domElement !== undefined) ? domElement : document;
    this.contextMenuClick = contextMenuClick;

    this.time = performance.now();

    this.enabled = true;
    this.rotateSpeed = 1.0;
    this.direction = new THREE.Vector3(0, 0, 0);
    this.up = new THREE.Vector3(0, 0, 1);

    this.orbit = new OrbitController(this, this.up);
    this.flyControls = new FlyMoveControls(this);
    this.mouse = new MouseConfig();

    // Used for touch controls
    this.touchHandler = this.touchHandler.bind(this);

    this.initScene();

    this.cameraAlignerAdv = new CameraAlignerAdv(this, this.alignerScene, props);
    this.cameraAlignerBasic = new CameraAlignerBasic(this, props);

    // Rotation/wheel movement
    this.wheelDelta = 0.0;
    this.phiDelta = 0;
    this.thetaDelta = 0;
    this.addEventListeners();

    this.update(null);
  }

  get mobile() {
    return (/android|webos|iphone|ipad|ipod|blackberry|playbook|BB10|iemobile|opera mini/i
      .test(navigator.userAgent.toLowerCase()));
  }

  get cameraList() {
    return this.viewer.cameraList;
  }

  get sceneLabels() {
    return this.viewer.imageLabels.sceneLabels;
  }

  get markers() {
    return this.viewer.markers;
  }

  get measurements() {
    return this.viewer.measurements;
  }

  get pointclouds() {
    return this.viewer.pointclouds;
  }

  get picker() {
    return this.viewer.gpuPickers.points;
  }

  get fov() {
    return this.object.fov;
  }

  set fov(value) {
    this.object.fov = value;
  }

  get angles() {
    return [this.thetaDelta, this.phiDelta];
  }

  set angles(values) {
    const [theta, phi] = values;

    if (theta != null) {
      this.thetaDelta = theta;
    }

    if (phi != null) {
      this.phiDelta = phi;
    }
  }

  addEventListeners() {
    let dom = this.domElement;

    // Add all event listeners
    dom.addEventListener('mousedown', this.onMouseDown.bind(this), false);
    dom.addEventListener('mouseup', this.onMouseUp.bind(this), false);
    dom.addEventListener('wheel', this.onMouseWheel.bind(this), false);
    dom.addEventListener('dblclick', this.onMouseDoubleClick.bind(this), false);
    dom.addEventListener('mousemove', this.toggleMouseMove.bind(this), false);

    // All touchevents get turned into mouse events
    dom.addEventListener('touchstart', this.touchHandler, true);
    dom.addEventListener('touchmove', this.touchHandler, true);
    dom.addEventListener('touchend', this.touchHandler, true);
    dom.addEventListener('touchcancel', this.touchHandler, true);
  }

  initEvents() {
  }

  initScene() {
    // Scene for aligner
    const alignerScene = new THREE.Scene();
    const alignerLight = new THREE.AmbientLight(0xFFFFFF);
    alignerScene.add(alignerLight);
    this.alignerScene = alignerScene;
  }

  rotateLeft(angle) {
    this.thetaDelta += angle;
  }

  rotateUp(angle) {
    this.phiDelta += angle;
    this.phiDelta = Math.min(this.phiDelta, THREE.MathUtils.degToRad(89));
    this.phiDelta = Math.max(this.phiDelta, THREE.MathUtils.degToRad(-89));
  }

  resetData() {
    this.orbit.controls.enabled = false;
    this.thetaDelta = 0;
    this.phiDelta = 0;
    this.orbit.state = false;
    this.direction = new THREE.Vector3(0, 0, 0);
    this.orbit.setUpVector(this.up);
    this.assetSelected = false;
  }

  updateFarPlane(far) {
    this.object.far = far;
    this.orbit.camera.far = far;
  }

  getCameraTarget() {
    let cameraPosition;
    if (this.orbit.controls.enabled) {
      cameraPosition = this.orbit.controls.target;
    } else {
      cameraPosition = this.object.position;
    }
    return cameraPosition;
  }

  setOrbitState(state, position = null) {
    this.viewer.updatePickers();

    if (!position) {
      // Value is somehow forced to become what it already is...
      if ((this.orbit.state) && (state)) {
        return;
      }
      if ((!this.orbit.state) && (!state)) {
        return;
      }
    }

    let newPosition;
    let pivotLocation;
    let smoothTransition = false;

    this.enabled = false;
    if (state) {
      // Set our orbit controls position and target
      if (!position) {
        newPosition = this.object.position;
        pivotLocation = new THREE.Vector3(0, 0, -1 * this.orbit.rotateLength);
        pivotLocation.applyQuaternion(this.object.quaternion);
        pivotLocation.add(newPosition);
      } else {
        newPosition = position.position;
        pivotLocation = position.pivot;
        smoothTransition = position.smooth;
      }

      this.orbit.setPositionValues(newPosition, pivotLocation,
        state, smoothTransition);

      this.viewer.camera = this.orbit.camera;

      if (!smoothTransition) {
        this.flyControls.disable();
      }
    } else {
      this.orbit.controls.toggleFlyMode(false, false);
      this.orbit.controls.enabled = state;
      this.viewer.camera = this.object;
    }

    this.orbit.state = state;
    this.enabled = true;

    // Set panoramic visibility
    if (this.cameraList) {
      this.cameraList.setMeshVisible(!state);
    }

    this.viewer.handleWindowResize();
    this.viewer.updatePickerCamera();
  }

  // Only ever called from other functions
  removeMouseUp() {
    this.mouse.upEnabled = false;
  }

  getPickerResults(event) {
    // Update gpu picker if required
    this.picker.update();

    // Grab our geometry index
    const index = this.picker.value(event);
    if (index === null) {
      return null;
    }

    // Convert index to closest coordinate
    const coordinate = this.picker.getClosestCoordinate(index);
    return coordinate;
  }

  onLongPress(event) {
    let newEvent = new MouseEvent('mouseup', {
      bubbles: true,
      cancelable: true,
      view: window,
      detail: 1,
      screenX: event.changedPointers[0].screenX, // event.screenX,
      screenY: event.changedPointers[0].screenY, // event.screenY,
      clientX: event.changedPointers[0].clientX, // event.clientX,
      clientY: event.changedPointers[0].clientY, // event.clientY,
      ctrlKey: false,
      altKey: false,
      shiftKey: false,
      metaKey: false,
      button: 2
    });
    this.domElement.dispatchEvent(newEvent);
  }

  onDoubleTap(event) {
    let pointer = event.changedPointers[0];
    let newEvent = new MouseEvent('dblclick', {
      bubbles: true,
      cancelable: true,
      view: window,
      detail: 1,
      screenX: pointer.screenX,
      screenY: pointer.screenY,
      clientX: pointer.clientX,
      clientY: pointer.clientY,
      ctrlKey: false,
      altKey: false,
      shiftKey: false,
      metaKey: false,
      button: 0
    });
    this.domElement.dispatchEvent(newEvent);
  }

  resetCamera(side="top") {
    let clouds = this.pointclouds;
    let bbox = clouds.combinedBoundingBox;
    if (!bbox) {
      return;
    }

    let newPosition = clouds.orbitPositionFromBounds(bbox, side, true);
    this.setOrbitState(true, newPosition);
  }

  toggleCamera() {
    let checked = this.orbit.status;
    this.setOrbitState(!checked);
  }

  toggleMouseMove(event) {
    this.mouse.moveEnabled = true;
    this.moveEvent = event;
  }

  onMouseMove(event) {
    if (!this.mouse.moveEnabled) {
      return;
    }

    const markers = this.markers;
    const sceneLabels = this.sceneLabels;
    const measurements = this.measurements;
    const aligner = this.cameraAlignerAdv;
    const compass = this.viewer.compass;

    let element = this.domElement === document ? this.domElement.body : this.domElement;
    this.mouse.moveEnabled = false;

    if (this.mouse.down) {
      this.mouse.moveEnd.set(event.clientX, event.clientY);
      let mouseMoveDistance = this.mouse.moveStart.distanceTo(this.mouse.moveEnd);
      this.mouse.moveDistance += mouseMoveDistance;
      this.mouse.moveDelta.subVectors(this.mouse.moveEnd, this.mouse.moveStart);
      this.mouse.moveStart.copy(this.mouse.moveEnd);
      this.mouse.movement = this.mouse.moveDistance > 0;
    }

    // Compass mousemove
    let hovering = compass.onMouseMove(event);
    if (hovering) {
      return;
    }

    // Marker mousemove
    markers.onMouseMove(event, this.mouse);

    // Measurements mousemove
    measurements.onMouseMove(event, this, this.mouse);
    if (measurements.movingEndpoint) {
      return;
    }

    // Scene labels mousemove
    sceneLabels.onMouseMove(event, this.mouse);
    if (sceneLabels.movingEndpoint) {
      return;
    }

    if (aligner.enabled) {
      aligner.updateSelectionText();
    }

    if (!this.mouse.movement) {
      return;
    }

    // Exit here if we are in 3d mode (or measurements are disabled somehow)
    if (!this.enabled || this.orbit.state) {
      return;
    }

    event.preventDefault();

    // Set the rotation
    let rotateDelta = new THREE.Vector2().copy(this.mouse.moveDelta);

    // Lessen the rotation when zoomed in
    rotateDelta.multiplyScalar(this.object.fov / 90);

    // Rotating across whole screen goes 360 degrees around
    this.rotateLeft(2 * Math.PI * rotateDelta.x / element.clientWidth * this.rotateSpeed);

    // Rotating up and down along whole screen attempts to go 360, but limited to 180
    this.rotateUp(Math.PI * rotateDelta.y / element.clientHeight * this.rotateSpeed);
  }

  onMouseDown(event) {
    event.preventDefault();
    this.mouse.down = true;
    this.mouse.button = event.button;

    // We always want the mouseup event to happen
    this.mouse.upEnabled = true;

    // Start rotation and listen to mousemove events
    this.mouse.moveStart.set(event.clientX, event.clientY);
    this.mouse.moveDistance = 0;
  }

  onMouseUp(event) {
    const measurements = this.measurements;
    const sceneLabels = this.sceneLabels;
    const aligner = this.cameraAlignerAdv;
    const compass = this.viewer.compass;

    this.mouse.down = false;

    // We are no longer grabbing measurement endpoints
    measurements.measureGrabFinished();

    // Might have been turned off while adjusting measurements
    this.orbit.toggle(true);

    if (!this.mouse.upEnabled || !this.enabled) {
      return;
    }

    let isRightClick = event.button === 2;
    let isLeftClick = event.button === 0;

    if (this.mouse.movement) {
      this.finishMouseUp();
      return;
    }

    if (isLeftClick) {
      let markerClicked = this.markers.mouseClick(event);
      if (markerClicked) {
        this.finishMouseUp();
        return;
      }

      let compassClicked = compass.mouseClick(event);
      if (compassClicked) {
        this.finishMouseUp();
        return;
      }
    }

    if (sceneLabels.enabled) {
      if (isLeftClick) {
        sceneLabels.mouseClick(event);
      } else if (isRightClick) {
        if (sceneLabels.drawing) {
          // Cancel scene labels
          sceneLabels.cancelLabel(event);
        } else {
          // Show context menu
          this.contextMenuClick(event);
        }
      }
    } else if (measurements.enabled) {
      if (isLeftClick) {
        measurements.mouseClick(event);
      } else if (isRightClick) {
        if (measurements.measuring) {
          // Cancel measurement
          measurements.cancelMeasurement(event);
        } else {
          // Show context menu
          this.contextMenuClick(event);
        }
      }
    } else {
      if (isLeftClick) {
        // Get direction vector
        if (aligner.enabled) {
          aligner.mouseClick(event);
        }
      } else if (isRightClick) {
        if (aligner.selecting) {
          // Cancel observation selection
          aligner.cancelAlignment();
        } else {
          // Show context menu
          this.contextMenuClick(event);
        }
      }
    }

    this.finishMouseUp();
  }

  onMouseWheel(event) {
    this.zooming = true;
    this.viewer.updatePickers();

    // Remember if we are scrolling (to stop updating pointcloud)
    this.mouse.scrolling = true;
    clearTimeout(this.mouse.wheelTimeout);
    this.mouse.wheelTimeout = setTimeout(() => {
      this.mouse.scrolling = false;
    }, this.mouse.wheelDelay);

    if (this.enabled === false) {
      return;
    }

    if (this.orbit.state) {
      return;
    }

    event.preventDefault();

    let delta = 0;
    if (event.deltaY > 0) {
      delta = -5;
    } else if (event.deltaY < 0) {
      delta = 5;
    }

    this.wheelDelta = delta;
  }

  onMouseDoubleClick(event) {
    if ((!this.enabled) || (this.measurements.enabled)) {
      return;
    }

    let coordinate = this.getPickerResults(event);
    if (coordinate === null) {
      return;
    }

    if (this.orbit.status && !this.orbit.flyModeEnabled) {
      // Move to new pointcloud location
      this.flyControls.start(coordinate, true);
    }
  }

  restrictOrbitMovement(value) {
    let orbit = this.orbit.controls;
    orbit.enableRotate = value;
    orbit.enablePan = value;
  }

  finishMouseUp() {
    let callback = () => {
      this.mouse.movement = false;
      this.orbit.movement = false;
      this.viewer.updatePickers();
    };

    if (this.orbit.controls.enabled) {
      // Function will be called when orbit controls stop rotating/panning
      this.orbit.movement = true;
      this.orbit.callback = callback;
    } else {
      // Call our function immediately
      callback();
    }

    this.removeMouseUp();
  }

  getCameraDistance() {
    let minDistance = 1.0;
    let distance = minDistance;

    if (this.orbit.state) {
      let orbitObject = this.orbit.controls;
      let cameraPosition = orbitObject.object.position;
      let cameraPivot = orbitObject.target;
      let pivotLength = cameraPivot.distanceTo(cameraPosition);
      distance = Math.max(minDistance, pivotLength);
    }

    return distance;
  }

  getPrecision(precision) {
    let approxDistance = this.getCameraDistance();

    if (precision === null) {
      precision = Math.abs((1 - (100 - approxDistance) / 100.0) * (0.95) + 0.05);
      precision = Math.min(precision, 100.00);
      precision = Math.max(precision, 0.05);
    }

    let multiplier = this.viewer.far / this.viewer.defaultFarPlane;
    precision = precision * multiplier;

    return precision;
  }

  touchHandler(event) {
    let mouseEvent = touchToMouse(this, event);
    if (!mouseEvent) {
      return;
    }

    event.preventDefault();
    event.changedTouches[0].target.dispatchEvent(mouseEvent);
  }

  rotateSynchronous(angle) {
    this.thetaDelta += angle;
    this.adjustLookat();
  }

  adjustLookat() {
    this.object.up = this.up;
    let phiDelta = this.phiDelta + THREE.MathUtils.degToRad(90);
    this.direction.x = Math.cos(this.thetaDelta) * Math.sin(phiDelta);
    this.direction.y = Math.sin(this.thetaDelta) * Math.sin(phiDelta);
    this.direction.z = -1 * Math.cos(phiDelta);
    this.direction.add(this.object.position);
    this.object.lookAt(this.direction);
    this.object.updateProjectionMatrix();

    let maxAngle = 360 * Math.PI / 180;
    this.thetaDelta = this.thetaDelta % maxAngle;
    this.phiDelta  = this.phiDelta % maxAngle;
  }

  updateView() {
    if (this.wheelDelta !== 0) { // controls mouse wheel zoom
      let fov = this.object.fov - this.wheelDelta;
      fov = Math.max(this.minFOV, Math.min(this.maxFOV, fov));
      this.fov = fov;
    }

    // Rotate our lookat vector
    this.adjustLookat();

    // Reset
    this.wheelDelta = 0;
    this.zooming = false;
  }

  update(changeDetector) {
    this.cameraAlignerAdv.update(changeDetector);
    this.onMouseMove(this.moveEvent);

    if (this.orbit.movement) {
      this.orbit.checkMovement();
    }

    // Update orbit performance tick
    let timeEnd = performance.now();
    let tick = timeEnd - this.time;
    this.time = timeEnd;
    this.orbit.controls.tick = tick;

    if (this.flyControls.enabled) {
      this.flyControls.move();
    }

    if (!this.enabled) {
      return;
    }

    this.orbit.update();
    this.updateView();
  }
}

class MouseConfig {
  private down: any;
  private button: any;
  private moveTolerance: any;
  private wheelTimeout: any;
  private wheelDelay: any;
  private endTimeout: any;
  private endDelay: any;
  private rotating: any;
  private zooming: any;
  private scrolling: any;
  private moveTick: any;
  private moveInterval: any;
  private moveEvent: any;
  private moveEnabled: any;
  private moveStart: any;
  private moveEnd: any;
  private moveDelta: any;
  private moveDistance: any;
  private upEnabled: any;

  constructor() {
    this.down = false;
    this.button = 0;
    this.moveTolerance = 10;

    this.wheelTimeout = null;
    this.wheelDelay = 150;

    this.endTimeout = 50;
    this.endDelay = 500;

    // Detect movement
    this.rotating = false;
    this.zooming = false;
    this.scrolling = false;

    this.moveTick = 100;
    this.moveInterval = null;
    this.moveEvent = null;
    this.moveEnabled = false;

    this.moveStart = new THREE.Vector2();
    this.moveEnd = new THREE.Vector2();
    this.moveDelta = new THREE.Vector2();
    this.moveDistance = 0;

    this.upEnabled = false;
  }
}

class FlyMoveControls {
  private panoControls: PanoControls;
  private maxSteps: any;
  private rotateLength: any;
  private heightAbove: any;
  private enabled: any;
  private step: any;
  private initial: SphericalPosition;
  private increment: SphericalPosition;
  private pivot: Vector3;

  constructor(parent) {
    this.panoControls = parent;

    this.maxSteps = 10;
    this.rotateLength = 2.0;
    this.heightAbove = 0.0;

    this.disable();
  }

  get viewer() {
    return this.panoControls.viewer;
  }

  enable(pivot, initial, increment) {
    this.enabled = true;
    this.step = 0;
    this.pivot = pivot;
    this.initial = initial;
    this.increment = increment;
  }

  disable() {
    this.enabled = false;
    this.step = null;
    this.initial = null;
    this.increment = null;
  }

  start(pivot, addHeight, final = null) {
    let orbit = this.panoControls.orbit;
    let current = this.viewer.camera.position;
    let totalIntervals = this.maxSteps;

    if (addHeight) {
      // We actually move above our clicked point, so we cant move around
      // easier after getting to this location;
      pivot.z += this.heightAbove;
    }

    if (final === null) {
      // Final position is our clicked values minus the rotate length
      let vector = new THREE.Vector3()
        .add(pivot)
        .sub(current)
        .setLength(this.rotateLength);

      // Calculate final position based on rotate length
      final = new THREE.Vector3().add(pivot).sub(vector);
    }

    let sphereStart = new SphericalPosition()
      .fromVectors(pivot, current);

    let sphereFinish = new SphericalPosition()
      .fromVectors(pivot, final);

    let dRadius = sphereFinish.radius - sphereStart.radius;
    let dPhi = sphereFinish.phi - sphereStart.phi;

    // Make sure the rotation stays between [-180, 180]
    let dTheta = sphereFinish.theta - sphereStart.theta;
    if (dTheta > 180) {
      dTheta = -1 * (360 - dTheta);
    } else if (dTheta < -180) {
      dTheta = dTheta + 360;
    }

    let increment = new SphericalPosition().fromValues(
      dRadius / totalIntervals,
      dTheta / totalIntervals,
      dPhi / totalIntervals,
    );

    // Initial starting point (current position, looking at final pivot)
    orbit.setPositionValues(current, pivot, true);

    this.enable(pivot, sphereStart, increment);
  }

  move() {
    let orbit = this.panoControls.orbit;
    let theta = this.initial.theta + this.increment.theta * this.step;
    let radius = this.initial.radius + this.increment.radius * this.step;
    let phi = this.initial.phi + this.increment.phi * this.step;

    let pos = new SphericalPosition()
      .fromValues(radius, theta, phi)
      .toVector3()
      .add(this.pivot);

    // Travelled our max distance
    if (this.step === this.maxSteps) {
      // Set the final position and pivot
      orbit.setPositionValues(pos, this.pivot, true);
      this.disable();
      return;
    }

    // Set position only. Increment step counter
    this.viewer.camera.position.set(pos.x, pos.y, pos.z);
    //this.pos.add(increment);
    this.step += 1;
  }
}

class OrbitController {
  private panoControls: PanoControls;
  private up: any;
  private rotateLength: any;
  private state: any;
  private movement: any;
  private camera: any;
  private controls: any;
  private orbit: any;
  private mouse: any;
  private flyControls: any;
  private callback: any;

  constructor(panoControls, up) {
    this.panoControls = panoControls;
    this.up = up;

    this.rotateLength = 10.0;
    this.state = false;
    this.movement = false;

    this.camera = this.panoControls.object.clone();
    this.camera.isPerspectiveCamera = true;
    this.camera.up.copy(this.up);

    this.controls = new CustomOrbitControls(this.camera,
      this.panoControls.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.15;
    this.controls.rotateSpeed = 0.50;
    this.controls.panSpeed = 0.50;
    this.controls.enabled = false;
  }

  get status() {
    return this.state;
  }

  get flyModeEnabled() {
    return this.controls.flyMode;
  }

  get loadingRestricted() {
    let orbit = this.orbit;
    let orbitMovement = (orbit.movement) && (orbit.status);

    if (this.mouse.scrolling) {
      return true;
    } else if (this.flyControls.enabled) {
      return true;
    } else if (orbitMovement) {
      return true;
    } else if (this.mouse.down) {
      return true;
    }

    return false;
  }

  set flyModeSpeed(value) {
    this.controls.setFlySpeed(value);
  }

  getPositionValues() {
    let pivot = null;
    let orbit = null;

    try {
      orbit = new SceneCoordinate(this.camera.position)
        .toDataProjection()
        .toArray();

      pivot = new SceneCoordinate(this.controls.target)
        .toDataProjection()
        .toArray();
    } catch {
      // Nothing
    }

    return {orbit, pivot};
  }

  setPositionValues(pos, pivot, enabled, smooth=false) {
    if (smooth) {
      this.panoControls.flyControls.start(pivot, false, pos);
    } else {
      this.controls.position0.set(pos.x, pos.y, pos.z);
      this.controls.target0.set(pivot.x, pivot.y, pivot.z);
      this.controls.reset();
    }

    this.controls.enabled = enabled;
  }

  setUpVector(upVector) {
    this.up = upVector;
    this.camera.up.copy(this.up);
  }

  update() {
    if (!this.state) {
      return;
    }

    this.controls.update();
  }

  object() {
    return this.controls;
  }

  toggle(value) {
    if (this.state) {
      this.controls.enabled = value;
    }
  }

  // Our orbit controls keep spinning/panning for a while after you release the
  // mouse button, so we need to find out when they are actually done rotating
  checkMovement() {
    let camera = this.controls;
    if (camera.isMoving() === false) {
      this.callback();
    }
  }
}

export class RayCaster {
  private debug: any;
  private initialized: any;
  private width: any;
  private height: any;
  private event: any;
  private camera: any;
  private raycaster: any;

  constructor(viewer: Viewer, event) {
    this.debug = false;
    this.initialized = false;
    this.width = viewer.width;
    this.height = viewer.height;
    this.event = event;
    this.camera = viewer.camera;

    this.init();
  }

  init() {
    let direction = this.vectorFromEvent(this.event);
    let raycaster = this.defaultRaycaster(direction);
    this.raycaster = raycaster;
  }

  setPrecision(precision) {
    if (!this.initialized) {
      return;
    }

    this.raycaster.params = {
      'Points': {threshold: precision},
      'Line': {threshold: precision}
    };
  }

  intersectObject(target) {
    return this.raycaster.intersectObject(target);
  }

  intersectObjects(targets, opts={}) {
    if (!this.initialized) {
      return [];
    }

    let defaults = {
      minDistance: null,
      sortByRay: false
    };

    let options = {...defaults, ...opts};

    let objects =  this.raycaster.intersectObjects(targets);

    if (options.minDistance !== null) {
      objects = objects.filter(item => item.distance > 0.10);
    }

    if (options.sortByRay) {
      objects.sort(function(a, b) {
        return a.distanceToRay - b.distanceToRay;
      });
    }

    return objects;
  }

  vectorFromEvent(event) {
    let uv = eventToPixel(event);
    uv.x = 2 * (uv.x / this.width) - 1;
    uv.y = 1 - 2 * (uv.y / this.height);
    uv.z = 0.5;

    let direction = this.raycastDirection(uv);
    return direction;
  }

  raycastDirection(vec) {
    vec.unproject(this.camera);
    vec.sub(this.camera.position).normalize();
    return vec;
  }

  defaultRaycaster(direction) {
    if (!direction) {
      return false;
    }

    let raycaster = new THREE.Raycaster();
    let origin = this.camera.position;
    raycaster.set(origin, direction);
    this.initialized = true;
    return raycaster;
  }
}

